import {
  DateTimePickerBase,
  DateTimePickerProps,
  defaultMaskOptions,
  floorDate,
  formatRange,
  FormatRangeFunction,
  FormatterProps,
  isStartOfDay,
  steps,
} from '@e2m-customer-portal/ui';
import { add, format, getISOWeek, isSameDay, parse } from 'date-fns';
import * as R from 'ramda';
import React from 'react';
import { useTranslation } from 'react-i18next';

const dateFormat = 'dd.MM.yyyy';

const round = (minutesStep?: number) =>
  floorDate({
    hoursStep: steps.hours,
    minutesStep: minutesStep ?? steps.minutes,
    secondsStep: steps.seconds,
  });

const formatter = ({ patterns: formatPatterns, dateOnly = false }: Readonly<FormatterProps>): FormatRangeFunction =>
  R.cond([
    // End time is the start of the very next day? Display as
    // * `02.06.2022, 24:00` or
    // * `02.06.2022`
    [
      (range) => isStartOfDay(new Date(range.end)) && isSameDay(range.start, add(range.end, { days: -1 })),
      (range) =>
        [
          format(range.start, formatPatterns.date),
          !dateOnly && [format(range.start, formatPatterns.time), '24:00'].join(formatPatterns.connector),
        ]
          .filter(Boolean)
          .join(', '),
    ],

    // End time is the start of another day? Display as
    // * `02.06.2022, 08:00 bis 07.06.2022, 24:00` or
    // * `02.06.2022 bis 07.06.2022`
    [
      (range) => isStartOfDay(new Date(range.end)) && !isSameDay(range.start, range.end),
      (range) =>
        [
          [format(range.start, formatPatterns.date), !dateOnly && format(range.start, formatPatterns.time)]
            .filter(Boolean)
            .join(', '),

          [format(add(range.end, { days: -1 }), formatPatterns.date), !dateOnly && '24:00'].filter(Boolean).join(', '),
        ].join(formatPatterns.connector),
    ],

    [R.T, formatRange({ patterns: formatPatterns, dateOnly })],
  ]);

export const calendarWeekFormat = (date: Date, options = {}, endDate?: Date): string => {
  const range = {
    start: date,
    end: endDate ?? add(date, { days: 6 }),
  };

  const isoWeeks = [range.start, range.end].map((date) => getISOWeek(date));
  const formatted = `${format(range.start, dateFormat, options)} - ${format(range.end, dateFormat, options)}`;

  return isoWeeks[0] !== isoWeeks[1]
    ? `KW ${isoWeeks[0]} - ${isoWeeks[1]} | ${formatted}`
    : `KW ${isoWeeks[0]} | ${formatted}`;
};

export const DateTimePicker: React.FC<DateTimePickerProps> = ({ timePickerProps = {}, ...props }) => {
  const [t] = useTranslation();

  return (
    <DateTimePickerBase
      errorTexts={{
        timeFromAfterTimeTo: t('TIME_PICKER_ERROR_TIME_FROM_AFTER_TIME_TO'),
      }}
      pickerLabels={{
        timeLabel: t('TIME'),
        timeFromLabel: t('TIME_FROM'),
        timeToLabel: t('TIME_TO'),
        allDayLabel: t('TIME_ALL_DAY'),
        cancelButtonLabel: t('SHARED_CANCEL'),
        submitButtonLabel: t('SHARED_SUBMIT'),
      }}
      formats={{
        range: {
          formatter,
          patterns: {
            connector: t('DATE_TIME_PICKER_FORMAT_RANGE_CONNECTOR'),
            date: dateFormat,
            time: 'HH:mm',
          },
        },
      }}
      timePickerProps={R.mergeDeepRight(
        {
          from: {
            className: 'h-full aria-invalid:bg-error-10',
          },
          to: {
            format: (date: Date) =>
              isStartOfDay(date) ? '24:00' : format(round(timePickerProps.to?.minutesStep)(date), 'HH:mm'),
            parse: (value: string) => {
              const [hours] = value.split(':');
              return parse(hours === '24' ? '00:00' : value, 'HH:mm', new Date());
            },
            className: 'h-full aria-invalid:bg-error-10',
            maskOptions: R.mergeDeepRight(defaultMaskOptions, {
              blocks: {
                HH: {
                  from: 0,
                  to: 24,
                },
              },
            }),
          },
        },
        timePickerProps,
      )}
      {...props}
    />
  );
};
